import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  Alert,
  Button,
  Flex,
  Icon,
  Text,
  Tooltip,
  openConfirmModal,
} from '@inflearn/ds-react-next';
import { faInfoCircle } from '@inflearn/pro-regular-svg-icons';
import { faEnvelope } from '@inflearn/pro-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { ApiError } from '../../../../../../../resources/ApiError';
import { logger } from '../../../../../../../resources/Logger';
import { useOpenEditCancelConfirmModal } from '../../../../../../hooks/useOpenEditCancelConfirmModal';
import useClientAuthChangeEmailVerificationCreateMutation from '../../../../../../mutations/useClientAuthChangeEmailVerificationCreateMutation';
import useClientAccountCredentialsQuery from '../../../../../../queries/useClientAccountCredentialsQuery';
import { setQueryDataUpdater } from '../../../../../../utils/setQueryDataUpdater';
import EmailValidator from '../../../../../../validators/EmailValidator';
import { SettingRow } from '../../common/SettingRow';
import type { BasicSectionRowProps } from '../BasicSection';
import { EmailFormItem, type EmailFormValue } from './EmailFormItem';

type EmailSettingRowProps = BasicSectionRowProps;

export const EmailSettingRow = ({
  showEditButton,
  onClose,
  onClick,
  isEditMode,
}: EmailSettingRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isSuccess } = useClientAccountCredentialsQuery({
    suspense: true,
    useErrorBoundary: false,
  });
  const form = useForm<EmailValidator>({
    mode: 'all',
    resolver: classValidatorResolver(EmailValidator),
    defaultValues: { email: data?.email ?? '' },
  });
  const { isDirty, isValid } = form.formState;
  const { openEditCancelConfirmModal } = useOpenEditCancelConfirmModal();
  const { mutate: submitEmailMutate, isLoading } =
    useClientAuthChangeEmailVerificationCreateMutation();

  const handleSubmit: SubmitHandler<EmailFormValue> = ({ email }) => {
    submitEmailMutate(
      {
        email,
      },
      {
        onSuccess: () => {
          /* 바꾼 즉시의 ReadOnly 컴포넌트는 변경 예정의 값으로 보이도록 처리 */
          queryClient.setQueryData(
            useClientAccountCredentialsQuery.queryKey,
            setQueryDataUpdater({
              ...data,
              email,
            }),
          );

          openConfirmModal({
            title: t('재인증 메일 발송'),
            children: (
              <Flex direction="column" gap={16}>
                <Alert icon={<Icon icon={faEnvelope} />} type="default" title={email} />
                <Text size="md" c="gray.7">
                  {t('위 이메일로 재인증 메일이 발송되었습니다.')}
                  <br />
                  {t('메일이 확인되지 않을 경우, 스팸함을 확인해 주세요.')}
                </Text>
              </Flex>
            ),

            withCloseButton: true,
            closeOnClickOutside: true,
            onConfirm: () => {
              onClose?.();
            },
            labels: {
              confirm: t('확인'),
            },
            footerType: 'right',
            confirmProps: {
              color: 'infgreen',
              type: 'submit',
              size: 'md',
            },
          });
        },
        onError: (error) => {
          if (
            ApiError.isApiError(error) &&
            (error.errorCode === 'SAME_EMAIL' || error.errorCode === 'EMAIL_ALREADY_IN_USE')
          ) {
            form.setError('email', {
              message: t('이미 사용중인 이메일 입니다.'),
            });

            return;
          }

          logger.error(error);
        },
      },
    );
  };

  const handleEditCancel = () => {
    if (!isDirty) {
      onClose?.();

      return;
    }

    openEditCancelConfirmModal({ onConfirm: () => onClose?.() });
  };

  useEffect(() => {
    if (!isEditMode) {
      form.clearErrors();
      form.reset();

      return;
    }

    if (!data) {
      return;
    }

    form.setValue('email', data.email);
  }, [isEditMode, data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (isSuccess) {
      form.setValue('email', data.email);
    }
  }, [data, isSuccess]);

  return (
    <SettingRow
      label={t('이메일')}
      labelRightSection={
        <Tooltip
          label={t('이메일 변경 후, 재인증이 필요합니다.')}
          position="bottom-start"
          events={{ hover: true, focus: true, touch: true }}
        >
          <Icon icon={faInfoCircle} color="gray.6" />
        </Tooltip>
      }
      align={isEditMode ? 'flex-start' : 'center'}
    >
      {isEditMode ? (
        <Flex
          w="100%"
          component="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          direction="column"
          gap={8}
        >
          <EmailFormItem
            showLabel={false}
            register={form.register}
            errors={form.formState.errors}
            size="md"
            autoFocus
          />

          <Flex gap={8} justify="end">
            <Button color="gray" variant="subtle" size="sm" radius="md" onClick={handleEditCancel}>
              {t('취소')}
            </Button>
            <Button
              type="submit"
              color="infgreen"
              variant="default"
              size="sm"
              radius="md"
              disabled={!isDirty || !isValid || isLoading}
              loading={isLoading}
            >
              {t('저장')}
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Flex w="100%" justify="space-between" align="center">
          <Flex gap={8} align="center">
            <Text
              fz={16}
              fw={600}
              c="gray.9"
              styles={{
                root: { wordBreak: 'break-all' },
              }}
            >
              {data?.email}
            </Text>
          </Flex>
          {showEditButton && (
            <Button variant="default" size="sm" radius="md" onClick={onClick}>
              {t('설정')}
            </Button>
          )}
        </Flex>
      )}
    </SettingRow>
  );
};
